import React from 'react';
import { Box, Container, Typography, Paper } from '@mui/material';
import PageHeader from '../components/common/PageHeader';

const PrivacyPolicy = () => {
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <PageHeader title="Privacy Policy" />
      <Paper
        sx={{
          p: 4,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ mb: 4 }}>
          <Typography variant="body1" paragraph>
            Last updated: August 17, 2025
          </Typography>

          <Typography variant="body1" paragraph>
            PT. Hakiki Inti Sejati ("we", "our", or "us") operates the Hakiki Project Management application (the "Service"). 
            This page informs you of our policies regarding the collection, use, and disclosure of personal data 
            when you use our Service and the choices you have associated with that data.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
            Information Collection and Use
          </Typography>
          <Typography variant="body1" paragraph>
            We collect several different types of information for various purposes to provide and improve our Service to you:
          </Typography>
          
          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
            Personal Data
          </Typography>
          <Typography variant="body1" paragraph>
            While using our Service, we may ask you to provide us with certain personally identifiable information 
            that can be used to contact or identify you ("Personal Data"). Personally identifiable information may include, but is not limited to:
          </Typography>
          <ul>
            <li>Email address</li>
            <li>First name and last name</li>
            <li>Phone number</li>
            <li>Company name</li>
            <li>Cookies and Usage Data</li>
          </ul>

          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
            Usage Data
          </Typography>
          <Typography variant="body1" paragraph>
            We may also collect information that your browser sends whenever you visit our Service or when you access the Service 
            by or through a mobile device ("Usage Data").
          </Typography>
          
          <Typography variant="body1" paragraph>
            This Usage Data may include information such as your computer's Internet Protocol address (e.g., IP address), 
            browser type, browser version, the pages of our Service that you visit, the time and date of your visit, 
            the time spent on those pages, unique device identifiers, and other diagnostic data.
          </Typography>
          
          <Typography variant="body1" paragraph>
            When you access the Service with a mobile device, this Usage Data may include information such as the type of mobile device you use, 
            your mobile device unique ID, the IP address of your mobile device, your mobile operating system, the type of mobile Internet browser you use, 
            unique device identifiers, and other diagnostic data.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
            Use of Data
          </Typography>
          <Typography variant="body1" paragraph>
            PT. Hakiki Inti Sejati uses the collected data for various purposes:
          </Typography>
          <ul>
            <li>To provide and maintain our Service</li>
            <li>To notify you about changes to our Service</li>
            <li>To allow you to participate in interactive features of our Service when you choose to do so</li>
            <li>To provide customer support</li>
            <li>To gather analysis or valuable information so that we can improve our Service</li>
            <li>To monitor the usage of our Service</li>
            <li>To detect, prevent and address technical issues</li>
          </ul>

          <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
            Data Storage and Transfer
          </Typography>
          <Typography variant="body1" paragraph>
            Your information, including Personal Data, may be transferred to — and maintained on — computers located outside of your state, province, 
            country, or other governmental jurisdiction where the data protection laws may differ from those of your jurisdiction.
          </Typography>
          
          <Typography variant="body1" paragraph>
            If you are located outside Indonesia and choose to provide information to us, please note that we transfer the data, 
            including Personal Data, to Indonesia and process it there.
          </Typography>
          
          <Typography variant="body1" paragraph>
            Your consent to this Privacy Policy followed by your submission of such information represents your agreement to that transfer.
          </Typography>
          
          <Typography variant="body1" paragraph>
            PT. Hakiki Inti Sejati will take all the steps reasonably necessary to ensure that your data is treated securely and in accordance with this Privacy Policy 
            and no transfer of your Personal Data will take place to an organization or a country unless there are adequate controls in place 
            including the security of your data and other personal information.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
            Security of Data
          </Typography>
          <Typography variant="body1" paragraph>
            The security of your data is important to us but remember that no method of transmission over the Internet 
            or method of electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your Personal Data, 
            we cannot guarantee its absolute security.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
            Your Data Protection Rights
          </Typography>
          <Typography variant="body1" paragraph>
            We aim to take reasonable steps to allow you to correct, amend, delete, or limit the use of your Personal Data.
          </Typography>
          
          <Typography variant="body1" paragraph>
            If you wish to be informed about what Personal Data we hold about you and if you want it to be removed from our systems, please contact us.
          </Typography>
          
          <Typography variant="body1" paragraph>
            In certain circumstances, you have the following data protection rights:
          </Typography>
          <ul>
            <li>The right to access, update or delete the information we have on you</li>
            <li>The right of rectification</li>
            <li>The right to object</li>
            <li>The right of restriction</li>
            <li>The right to data portability</li>
            <li>The right to withdraw consent</li>
          </ul>

          <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
            Changes to This Privacy Policy
          </Typography>
          <Typography variant="body1" paragraph>
            We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page.
          </Typography>
          
          <Typography variant="body1" paragraph>
            We will let you know via email and/or a prominent notice on our Service, prior to the change becoming effective 
            and update the "effective date" at the top of this Privacy Policy.
          </Typography>
          
          <Typography variant="body1" paragraph>
            You are advised to review this Privacy Policy periodically for any changes. 
            Changes to this Privacy Policy are effective when they are posted on this page.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
            Contact Us
          </Typography>
          <Typography variant="body1" paragraph>
            If you have any questions about this Privacy Policy, please contact us:
          </Typography>
          <ul>
            <li>By email: hakiki@prefab-hakiki.id</li>
            <li>By phone: +62 82422753</li>
            <li>By mail: Jl. Nusa Indah VI, B498, Jakasetia, South Bekasi, West Java, Indonesia</li>
          </ul>

          <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid #ddd' }}>
            <Typography variant="body2" color="text.secondary">
              © 2025 PT. Hakiki Inti Sejati. All rights reserved.
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default PrivacyPolicy;